import {
    BuildingStorefrontIcon,
    BanknotesIcon,
    ClipboardDocumentListIcon,
    ClockIcon,
    CreditCardIcon,
    DocumentTextIcon,
    HomeIcon,
    MapPinIcon,
    ShoppingCartIcon,
    UserGroupIcon,
    UserCircleIcon,
} from '@heroicons/react/24/outline';
import {
    BanknotesIcon as BanknotesSolid,
    BuildingStorefrontIcon as BuildingStorefrontSolid,
    ClipboardDocumentListIcon as ClipboardDocumentListSolid,
    ClockIcon as ClockSolid,
    CreditCardIcon as CreditCardSolid,
    DocumentTextIcon as DocumentTextSolid,
    HomeIcon as HomeSolid,
    MapPinIcon as MapPinSolid,
    ShoppingCartIcon as ShoppingCartSolid,
    UserCircleIcon as UserCircleSolid,
    UserGroupIcon as UserGroupSolid,
} from '@heroicons/react/24/solid';
import { ALL_ROLES, OWNER_ABOVE, RoleName, STAFF_ABOVE } from '../../constants/roles';
import type { IconType } from '../ui/Chip';

export interface NavItem {
    id: string;
    to: string;
    /** i18n key, common namespace */
    labelKey: string;
    icon: IconType;
    iconActive: IconType;
    roles: readonly RoleName[];
    /** Orders carries the pending-count badge. */
    badge?: boolean;
    /** Divider above, in the sidebar. */
    separated?: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
    { id: 'home', to: '/', labelKey: 'nav.home', icon: HomeIcon, iconActive: HomeSolid, roles: STAFF_ABOVE },
    { id: 'orders', to: '/orders', labelKey: 'nav.orders', icon: ShoppingCartIcon, iconActive: ShoppingCartSolid, roles: ALL_ROLES, badge: true },
    { id: 'menus', to: '/menus', labelKey: 'nav.menus', icon: DocumentTextIcon, iconActive: DocumentTextSolid, roles: STAFF_ABOVE },
    { id: 'items', to: '/menu-items', labelKey: 'nav.menuItems', icon: ClipboardDocumentListIcon, iconActive: ClipboardDocumentListSolid, roles: STAFF_ABOVE },
    { id: 'locations', to: '/locations', labelKey: 'nav.locations', icon: MapPinIcon, iconActive: MapPinSolid, roles: STAFF_ABOVE },
    { id: 'activity', to: '/activity', labelKey: 'nav.activity', icon: ClockIcon, iconActive: ClockSolid, roles: STAFF_ABOVE },
    { id: 'plan', to: '/plan', labelKey: 'nav.plan', icon: CreditCardIcon, iconActive: CreditCardSolid, roles: OWNER_ABOVE },
    { id: 'venue', to: '/restaurants', labelKey: 'nav.venue', icon: BuildingStorefrontIcon, iconActive: BuildingStorefrontSolid, roles: ['Owner'] },
    { id: 'team', to: '/users', labelKey: 'nav.team', icon: UserGroupIcon, iconActive: UserGroupSolid, roles: OWNER_ABOVE },
    { id: 'restaurants', to: '/restaurants', labelKey: 'nav.restaurants', icon: BuildingStorefrontIcon, iconActive: BuildingStorefrontSolid, roles: ['Admin'], separated: true },
    { id: 'statements', to: '/statements', labelKey: 'nav.statements', icon: BanknotesIcon, iconActive: BanknotesSolid, roles: ['Admin'] },
];

/** Staff has no Home (it needs menus and locations data) and lives in Orders and Profile. */
export const PROFILE_ITEM: NavItem = {
    id: 'profile',
    to: '/profile',
    labelKey: 'nav.profile',
    icon: UserCircleIcon,
    iconActive: UserCircleSolid,
    roles: ALL_ROLES,
};

/** The Owner's Venue entry opens their own restaurant; without one the entry is left out. */
export const navFor = (role: RoleName | undefined, restaurantId?: string | null): NavItem[] =>
    NAV_ITEMS.filter((i) => role && i.roles.includes(role) && (i.id !== 'venue' || restaurantId)).map((i) =>
        i.id === 'venue' ? { ...i, to: `/restaurants/${restaurantId}` } : i
    );

/** Bottom bar: up to four destinations plus More; Staff get Orders and Profile only. */
export const bottomNavFor = (role: RoleName | undefined, restaurantId?: string | null): { tabs: NavItem[]; more: NavItem[] } => {
    const all = navFor(role, restaurantId);
    if (role === 'Staff') return { tabs: [...all, PROFILE_ITEM], more: [] };
    const tabIds = ['orders', 'menus', 'items', 'locations'];
    return {
        tabs: all.filter((i) => tabIds.includes(i.id)),
        more: all.filter((i) => !tabIds.includes(i.id)),
    };
};
